import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { assessPairSize } from "./pair-size";
import { loadSamplePair, SAMPLE_DESCRIPTION } from "./sample-exports";
import { GuideStepper } from "../guide/GuideStepper";
import { FileDropZone } from "./FileDropZone";
import { BatchPanel } from "../batch/BatchPanel";
import { HelpButton } from "../help/HelpDrawer";
import { Link, useNavigate } from "react-router-dom";
import { DateOrderingAlert } from "../../components/upload/DateOrderingAlert";
import { ExportDateIndicators } from "../../components/upload/ExportDateIndicators";
import { ANALYSIS_CACHE_SCHEMA_VERSION, db, putAnalysisBounded } from "../../db";
import { ENGINE_SEMANTICS_VERSION } from "../../engine/version";
import { BELLINGHAM_PROCUREWARE, getProfile, listProfiles } from "../../profiles";
import { useEffectiveProfile } from "../profiles/use-effective-profile";
import { GenerateProfilePanel } from "./GenerateProfilePanel";
import { useDraftProfiles } from "./use-draft-profiles";
import type { ResolvedDraftProfile } from "../../profiles/resolve-draft";
import { ProfilePicker } from "./ProfilePicker";
import { loadLastProfileId, saveLastProfileId } from "./last-profile";
import { hashText } from "../../lib/hash";
import { parseExport } from "../../lib/file-order";
import { assessFileOrder } from "../../engine/export-metadata";
import { PROFILES } from "../../profiles";
import { describeDetectionMatch, detectSourceProfile, type DetectionResult } from "../../profiles/detect";
import { useUiStore } from "../../stores/ui-store";
import { useToastStore } from "../../stores/toast-store";
import { createAnalysisRunner } from "./analysis-runner";
import type { AnalyzeRequest } from "../../workers/protocol";

const worker = new Worker(new URL("../../workers/analysis.worker.ts", import.meta.url), { type: "module" });
// Owns request/response correlation: every worker message is dispatched by the
// analysisKey it echoes, so a result can never be handled — or cached — under a
// different run's key.
const runner = createAnalysisRunner(worker);

function parseCsvInput(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Put a programmatically loaded file into its picker, so the control does not
 * read "No file chosen" beside a page that has plainly chosen one.
 *
 * `input.files` is assignable only from a `DataTransfer` list. Where that is
 * unavailable — older engines, and jsdom — the page is still correct: React
 * state is what everything downstream reads, and the file name is rendered
 * under the control either way. So this is best-effort by design and must never
 * throw.
 */
function showInPicker(input: HTMLInputElement | null, file: File): void {
  if (input === null || typeof DataTransfer === "undefined") return;
  try {
    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
  } catch {
    // Assignment refused; the rendered file name below the control stands.
  }
}

export function UploadPage() {
  const navigate = useNavigate();
  const [baselineFile, setBaselineFile] = useState<File | null>(null);
  const [latestFile, setLatestFile] = useState<File | null>(null);
  const [loadingSample, setLoadingSample] = useState(false);
  const [pairOrderedBy, setPairOrderedBy] = useState<"filename" | "arrival" | null>(null);
  const [crossSourceReason, setCrossSourceReason] = useState("");
  const [crossSourceAcknowledged, setCrossSourceAcknowledged] = useState(false);
  const baselineInputRef = useRef<HTMLInputElement | null>(null);
  const latestInputRef = useRef<HTMLInputElement | null>(null);
  const [collectionPath, setCollectionPath] = useState(BELLINGHAM_PROCUREWARE.collectionPath);
  const [identityKeys, setIdentityKeys] = useState(BELLINGHAM_PROCUREWARE.quality.identityDefault.join(","));
  const [ignoredFields, setIgnoredFields] = useState("");
  // Set when the user edits a profile-derived input after the last profile
  // change; a customized value survives a re-render but not a profile switch.
  const [collectionPathCustomized, setCollectionPathCustomized] = useState(false);
  const [identityCustomized, setIdentityCustomized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAlert, setPendingAlert] = useState(false);
  const [running, setRunning] = useState(false);
  const step = useUiStore((state) => state.workerStep);
  const setStep = useUiStore((state) => state.setWorkerStep);
  const setAnalysis = useUiStore((state) => state.setAnalysis);
  const setReview = useUiStore((state) => state.setReview);
  const setInputTexts = useUiStore((state) => state.setInputTexts);
  // Last-used profile, validated against the registry: a stored id from a
  // removed profile falls back to the default rather than crashing.
  const [sourceProfileId, setSourceProfileId] = useState(() => {
    const stored = loadLastProfileId();
    return stored !== null && getProfile(stored) !== null ? stored : BELLINGHAM_PROCUREWARE.id;
  });
  const sourceProfile = getProfile(sourceProfileId) ?? BELLINGHAM_PROCUREWARE;
  // Where the current selection came from. Detection may replace a default or
  // persisted selection; it must never replace a person's (or its own,
  // subsequently reviewed) choice — it warns instead.
  const [selectionOrigin, setSelectionOrigin] = useState<"default" | "persisted" | "manual" | "detected">(
    () => (loadLastProfileId() !== null ? "persisted" : "default")
  );
  // Both of these belong to a PAIR of files, so they are stored with the pair
  // they were computed for and derived below. Clearing them in the parse effect
  // instead would leave one render showing the previous pair's detection —
  // which is a render in which Analyze reads as released, under a profile
  // detected from files nobody has now.
  const [detectionRead, setDetectionRead] = useState<{
    pairId: string;
    result: { baseline: DetectionResult; latest: DetectionResult };
  } | null>(null);
  // The parsed pair, kept from the effect below so the generate panel can
  // measure without reading and parsing both files a second time.
  const [parsedPair, setParsedPair] = useState<{ baseline: unknown; latest: unknown } | null>(null);
  const [generating, setGenerating] = useState(false);
  /**
   * The measured profile governing this run, when one is active.
   *
   * Held here rather than in the repo registry: that registry is a synchronous
   * module constant node tooling also imports, and it should keep meaning
   * "committed policy". A draft takes precedence over the picker's selection
   * while it is active, and picking a profile by hand clears it.
   */
  const [activeDraft, setActiveDraft] = useState<ResolvedDraftProfile | null>(null);
  const { drafts, saveDraft } = useDraftProfiles();
  /**
   * Picker rows: committed profiles plus any locally measured drafts.
   *
   * A draft that governs a run has to be visible in the control that names the
   * governing policy. Leaving it out let the picker read "Bellingham
   * ProcureWare · v9" while a draft actually governed the run — the precise
   * confusion this whole feature exists to prevent.
   */
  const profileRows = useMemo(
    () => [
      ...listProfiles(),
      ...drafts.map((draft) => ({
        id: draft.id,
        displayName: draft.displayName ?? draft.id,
        sourceUrl: draft.sourceUrl,
        version: draft.version
      }))
    ],
    [drafts]
  );
  const draftIds = useMemo(() => new Set(drafts.map((draft) => draft.id)), [drafts]);
  /** The pair whose mismatch notice was dismissed; a new pair is not dismissed. */
  const [mismatchDismissedFor, setMismatchDismissedFor] = useState<string | null>(null);
  /**
   * Identity of the loaded pair. Name, size and modification time together:
   * two different files can share a name, and the same file re-picked after an
   * edit is a different pair to analyse.
   */
  const pairId =
    baselineFile && latestFile
      ? `${baselineFile.name}:${baselineFile.size}:${baselineFile.lastModified}` +
        `|${latestFile.name}:${latestFile.size}:${latestFile.lastModified}`
      : "";
  const detection = detectionRead !== null && detectionRead.pairId === pairId ? detectionRead.result : null;
  const mismatchDismissed = pairId !== "" && mismatchDismissedFor === pairId;

  // Refs so the file-parse effect can read the CURRENT origin/selection
  // without re-parsing both files every time either changes.
  const selectionRef = useRef({ origin: selectionOrigin, profileId: sourceProfileId });
  selectionRef.current = { origin: selectionOrigin, profileId: sourceProfileId };
  // Same reason: the parse effect must see the current drafts without listing
  // them as a dependency and re-parsing both files whenever one is saved.
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  // Resolved policy identity: base + delta + any local override. What the
  // worker receives and what the cache key pins. `loading` gates Analyze so a
  // run can never start under a policy whose override read has not settled.
  const {
    profile: resolvedProfileOrNull,
    overrideActive,
    overrideStale,
    loading: profileResolving
  } = useEffectiveProfile(sourceProfile.id);
  /**
   * The policy this run will actually use.
   *
   * An active draft wins over the picker's selection. Routing it through this
   * one seam means the cache key, the worker payload, the safe-backfill display
   * and the export gate all see the draft without any of them needing to know
   * drafts exist — and the gate blocks the recovered artifact on its stamp.
   */
  const resolvedProfile = activeDraft ?? resolvedProfileOrNull;
  const showToast = useToastStore((state) => state.showToast);
  const fileOrderAssessment = useUiStore((state) => state.fileOrderAssessment);
  const setFileOrderAssessment = useUiStore((state) => state.setFileOrderAssessment);
  /**
   * True when detection recognized neither file and the selection is still the
   * one the page seeded rather than one a person chose.
   *
   * The seed is last-used, else Bellingham — the only profile in the fleet
   * carrying real backfill approvals and a 0.95 match-rate floor. Running an
   * unrecognized source under it means governing a stranger's data by another
   * source's approvals, with nothing in the way but a banner someone has to
   * read. So Analyze waits for a deliberate choice instead.
   *
   * Choosing the seeded profile from the picker is a deliberate choice and
   * clears this: what matters is that a person decided, not which id they
   * landed on.
   */
  /**
   * The two files matched DIFFERENT registered sources.
   *
   * No profile governs such a comparison — identity keys, backfill approvals
   * and the match-rate floor all belong to one source — so this blocks the run
   * rather than warning beside it, which is what it used to do. Someone can
   * still proceed, with a stated reason that reaches the artifacts.
   */
  const crossSourcePair =
    detection !== null &&
    detection.baseline.status === "match" &&
    detection.latest.status === "match" &&
    detection.baseline.match.profileId !== detection.latest.match.profileId;

  const unidentifiedSource =
    detection !== null &&
    detection.baseline.status !== "match" &&
    detection.latest.status !== "match" &&
    selectionOrigin !== "manual" &&
    activeDraft === null;

  // Known before a byte is parsed: File.size decides whether a pair is one this
  // browser can hold. See pair-size.ts for the measurements behind the numbers.
  const pairSize = useMemo(
    () => assessPairSize(baselineFile?.size ?? null, latestFile?.size ?? null),
    [baselineFile, latestFile]
  );
  const pairTooLarge = pairSize.level === "refuse";

  // Includes `running`: a second Analyze while one is live would either interleave
  // two runs on one worker or be refused by the runner — disable it instead.
  const disabled = useMemo(
    () =>
      !baselineFile ||
      !latestFile ||
      !fileOrderAssessment ||
      running ||
      profileResolving ||
      unidentifiedSource ||
      pairTooLarge ||
      (crossSourcePair && !(crossSourceAcknowledged && crossSourceReason.trim().length > 0)),
    [
      baselineFile,
      fileOrderAssessment,
      latestFile,
      running,
      profileResolving,
      unidentifiedSource,
      pairTooLarge,
      crossSourcePair,
      crossSourceAcknowledged,
      crossSourceReason
    ]
  );
  const dateOrderingIssues = fileOrderAssessment?.issues ?? [];
  const baselineExportDates = fileOrderAssessment?.baseline.dates ?? {};
  const latestExportDates = fileOrderAssessment?.latest.dates ?? {};

  // An abandoned run must not outlive the page: without this, a slow run started
  // here kept running after navigation and its stale closure could fire
  // setAnalysis + navigate('/results') minutes later, mid-configuration.
  useEffect(() => {
    return () => {
      runner.cancel();
    };
  }, []);

  // Derive the comparison inputs from the selected profile. Unconditional on a
  // profile switch — a manual edit surviving into a different source's analysis
  // is exactly the cross-source confusion to prevent; the badges below give a
  // deliberate edit an escape hatch instead.
  // Stable identity: it closes over nothing but setters and pure imports, so it
  // can be named honestly as a dependency of the parse effect below instead of
  // being omitted from it.
  const applyProfileSelection = useCallback((id: string, origin: "manual" | "detected") => {
    const next = getProfile(id);
    if (!next) return;
    // Picking a committed profile is a decision to run under it, so it retires
    // any draft that was governing the run.
    setActiveDraft(null);
    setSourceProfileId(id);
    setSelectionOrigin(origin);
    saveLastProfileId(id);
    setCollectionPath(next.collectionPath);
    setIdentityKeys(next.quality.identityDefault.join(","));
    setCollectionPathCustomized(false);
    setIdentityCustomized(false);
  }, []);
  /**
   * Selecting a draft activates it; selecting a committed profile retires any
   * draft (applyProfileSelection does that). Either way the choice is manual,
   * which is what releases the analysis.
   */
  const selectProfile = (id: string) => {
    const draft = drafts.find((entry) => entry.id === id);
    if (draft) {
      setActiveDraft(draft);
      setSelectionOrigin("manual");
      setCollectionPath(draft.collectionPath);
      setIdentityKeys(draft.quality.identityDefault.join(","));
      setCollectionPathCustomized(false);
      setIdentityCustomized(false);
      return;
    }
    applyProfileSelection(id, "manual");
  };

  // Picking a different file abandons the run in flight — its result would
  // describe files the user has moved past, and the runner refusing "already
  // running" would otherwise block the new configuration.
  /**
   * Load the sample pair as if it had been picked from the file dialog: same
   * state, same detection, same analysis. The tour must exercise the real path
   * or it demonstrates something the person cannot reproduce with their own
   * files.
   */
  const loadSample = async () => {
    setLoadingSample(true);
    abandonActiveRun();
    setError(null);
    setFileOrderAssessment(null);
    try {
      const { baseline, latest } = await loadSamplePair();
      setBaselineFile(baseline);
      setLatestFile(latest);
      showInPicker(baselineInputRef.current, baseline);
      showInPicker(latestInputRef.current, latest);
      setPairOrderedBy(null);
    } catch (sampleError) {
      setError(sampleError instanceof Error ? sampleError.message : "The sample data could not be loaded.");
    } finally {
      setLoadingSample(false);
    }
  };

  /**
   * Load a pair chosen by dropping files or a folder, on the same path as the
   * pickers. `orderedBy` is recorded so the page can say whether the direction
   * was established from the filenames or merely taken as given.
   */
  const loadDroppedPair = (reference: File, candidate: File, orderedBy: "filename" | "arrival") => {
    abandonActiveRun();
    setError(null);
    setFileOrderAssessment(null);
    setBaselineFile(reference);
    setLatestFile(candidate);
    showInPicker(baselineInputRef.current, reference);
    showInPicker(latestInputRef.current, candidate);
    setPairOrderedBy(orderedBy);
  };

  const abandonActiveRun = () => {
    if (runner.isRunning()) {
      runner.cancel();
      setRunning(false);
      setStep(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    // The one thing that cannot be derived: the file-order banner reads this
    // from the shared store, so a new pair has to clear what the last one put
    // there. Detection and the dismissal are derived from `pairId` instead.
    setFileOrderAssessment(null);
    if (!baselineFile || !latestFile) return;

    void Promise.all([baselineFile.text(), latestFile.text()])
      .then(([baselineText, latestText]) => {
        if (cancelled) return;
        // Each file is parsed ONCE here and the parsed value feeds both the
        // file-order assessment and profile detection — strictly cheaper than
        // the previous parse-inside-assess, which detection would have doubled.
        const parseNamed = (text: string, name: string) => {
          try {
            return parseExport(text);
          } catch {
            throw new Error(`“${name}” is not valid JSON.`);
          }
        };
        const baselineParsed = parseNamed(baselineText, baselineFile.name);
        const latestParsed = parseNamed(latestText, latestFile.name);
        setFileOrderAssessment(
          assessFileOrder(baselineParsed, latestParsed, baselineFile.name, latestFile.name, collectionPath)
        );

        setParsedPair({ baseline: baselineParsed, latest: latestParsed });

        // Drafts detect exactly like committed profiles: they carry the same
        // identityValues, measured from the files that produced them. So a
        // source onboarded once is recognized on every later upload without
        // anyone re-measuring it.
        const registered = [...Object.values(PROFILES), ...draftsRef.current];
        const baselineDetection = detectSourceProfile(baselineParsed, registered);
        const latestDetection = detectSourceProfile(latestParsed, registered);
        setDetectionRead({ pairId, result: { baseline: baselineDetection, latest: latestDetection } });

        const detectedDraft =
          baselineDetection.status === "match"
            ? (draftsRef.current.find((draft) => draft.id === baselineDetection.match.profileId) ?? null)
            : null;
        if (detectedDraft) setActiveDraft(detectedDraft);

        // Auto-select only over a default or persisted selection. A manual
        // choice — or a previous detection the user has had a chance to see —
        // is never silently replaced; the mismatch notice below warns instead.
        const { origin } = selectionRef.current;
        if (baselineDetection.status === "match" && (origin === "default" || origin === "persisted")) {
          applyProfileSelection(baselineDetection.match.profileId, "detected");
        }
      })
      .catch((problem: unknown) => {
        if (!cancelled) {
          setFileOrderAssessment(null);
          // parseNamed names the offending file; anything else (an unreadable
          // File, say) falls back to the generic phrasing.
          setError(
            problem instanceof Error && problem.message.includes("not valid JSON")
              ? problem.message
              : "Could not read export dates because one of the selected files is not valid JSON."
          );
        }
      });

    return () => {
      cancelled = true;
    };
    // `pairId` is derived from the two files, so it changes exactly when they
    // do: naming it costs no extra runs and keeps the list honest.
  }, [applyProfileSelection, baselineFile, collectionPath, latestFile, pairId, setFileOrderAssessment]);

  const runAnalysis = async () => {
    if (!baselineFile || !latestFile || !resolvedProfile) return;
    setError(null);
    try {
      const baselineText = await baselineFile.text();
      const latestText = await latestFile.text();
      // The file-order assessment already in the store was computed from these same
      // files when they were selected (the Analyze button is disabled until it
      // exists). Recomputing it here parsed both files a second time on the main
      // thread — pure jank at large file sizes.
      const identityFields = parseCsvInput(identityKeys);
      const ignored = parseCsvInput(ignoredFields);
      const analysisKey = await hashText(
        [
          await hashText(baselineText),
          await hashText(latestText),
          collectionPath,
          identityFields.join("|"),
          ignored.join("|"),
          // The policy hash pins the full resolved policy — base + delta +
          // any local override, quality section included — so ANY policy
          // change invalidates the cache rather than reusing the previous
          // policy's outcome. (It subsumes the old defaultProfile and
          // version entries.)
          resolvedProfile.id,
          resolvedProfile.policyHash,
          // A cached entry written under an older persisted shape must be a cache
          // miss, not a review missing fields the current code assumes exist.
          String(ANALYSIS_CACHE_SCHEMA_VERSION),
          // An engine BEHAVIOR fix (same shape, different results) must also be a
          // cache miss — otherwise stale wrong results are served indefinitely.
          String(ENGINE_SEMANTICS_VERSION),
          // The cross-source reason is quoted verbatim in the artifacts, so two
          // runs of the same pair with different reasons are different runs.
          // Without this the second would be served the first one's review and
          // the report would quote a reason nobody gave.
          crossSourcePair ? crossSourceReason.trim() : ""
        ].join("::")
      );

      // The cache is an optimization, never a requirement: a failing IndexedDB
      // (private browsing, corrupted DB) must degrade to a cache miss, not block
      // the analysis — every other db call in this file already degrades this way.
      const cached = await db.analyses.get(analysisKey).catch(() => undefined);
      if (cached) {
        setAnalysis(cached.result);
        setReview(cached.review ?? null);
        // The run id goes in the URL so a refresh, a bookmark or a shared link
        // reopens THIS run rather than whichever is newest.
        navigate(
          `/results?run=${encodeURIComponent(analysisKey)}&tab=${cached.result.qualityIssues.some((issue) => ["critical", "high"].includes(issue.severity)) ? "overview" : "records"}`
        );
        return;
      }

      const request: AnalyzeRequest = {
        type: "analyze",
        payload: {
          baselineFileName: baselineFile.name,
          latestFileName: latestFile.name,
          baselineText,
          latestText,
          analysisKey,
          config: {
            collectionPath,
            identityFields,
            ignoredFields: ignored,
            profileId: resolvedProfile.id
          },
          sourceProfile: resolvedProfile,
          ...(crossSourcePair && detection?.baseline.status === "match" && detection.latest.status === "match"
            ? {
                crossSourceOverride: {
                  baselineProfileId: detection.baseline.match.profileId,
                  latestProfileId: detection.latest.match.profileId,
                  reason: crossSourceReason.trim(),
                  acknowledgedAt: new Date().toISOString()
                }
              }
            : {})
        }
      };

      const started = runner.start(request, {
        onProgress: setStep,
        onError: (message) => {
          setRunning(false);
          setError(message);
        },
        onResult: async (payload) => {
          setRunning(false);
          setStep("Ready");
          const { analysis, review } = payload;
          setAnalysis(analysis);
          setReview(review);
          // Kept only so a session bundle can embed them; cleared with the run.
          setInputTexts([
            { role: "reference", fileName: baselineFile.name, text: baselineText },
            { role: "candidate", fileName: latestFile.name, text: latestText }
          ]);
          try {
            // Keyed by the analysisKey the worker ECHOED, which the runner guarantees
            // is the one this request was started with — a result can never be
            // cached under a different run's key. Bounded: old entries are pruned
            // and quota exhaustion evicts-then-retries instead of failing forever.
            await putAnalysisBounded({
              analysisKey: payload.analysisKey,
              createdAt: new Date().toISOString(),
              result: analysis,
              review
            });
          } catch {
            showToast("Result saved in this session but not cached in browser storage.", "warning");
          }
          navigate(
            `/results?run=${encodeURIComponent(payload.analysisKey)}&tab=${analysis.qualityIssues.some((issue) => ["critical", "high"].includes(issue.severity)) ? "overview" : "records"}`
          );
        }
      });
      if (!started) {
        setError("An analysis is already running. Wait for it to finish.");
        return;
      }
      setRunning(true);
    } catch (analysisError) {
      setRunning(false);
      setError(analysisError instanceof Error ? analysisError.message : "Failed to analyze files");
    }
  };

  const onAnalyze = () => {
    if (dateOrderingIssues.length > 0) {
      setPendingAlert(true);
      return;
    }
    void runAnalysis();
  };

  return (
    <main className="mx-auto max-w-5xl space-y-6 p-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">JSON Data Drift Analyzer</h1>
          <p className="text-sm text-slate-600">Upload baseline and latest exports to detect drift, quality failures, and document-level regressions.</p>
        </div>
        <HelpButton topicId="upload" label="Help for loading a pair" />
      </header>
      {/* Nothing after Review can be known here — this page has no analysis —
          so those steps read as ahead rather than as failed. */}
      <GuideStepper
        state={{
          filesLoaded: baselineFile !== null && latestFile !== null,
          profileChosen: !unidentifiedSource,
          analysisPresent: false,
          decisions: null,
          exported: false
        }}
      />
      <FileDropZone onPair={loadDroppedPair} disabled={running} />
      {pairOrderedBy === null ? null : (
        <p className="text-xs text-slate-600" data-testid="pair-order-note">
          {pairOrderedBy === "filename"
            ? "Order taken from the run timestamps in the filenames: the older run is the baseline."
            : "The filenames do not carry run timestamps, so the files were loaded in the order given. Check the export dates below."}
        </p>
      )}
      <section className="grid gap-4 md:grid-cols-2">
        <label className="rounded-sm border border-slate-300 bg-white p-4">
          <span className="text-sm font-medium">Baseline JSON</span>
          <input
            data-testid="baseline-input"
            ref={baselineInputRef}
            className="mt-2 block w-full text-sm"
            type="file"
            accept="application/json"
            onChange={(event) => {
              abandonActiveRun();
              setError(null);
              setFileOrderAssessment(null);
              setPairOrderedBy(null);
              setBaselineFile(event.target.files?.[0] ?? null);
            }}
          />
          {baselineFile ? <p className="mt-2 text-xs text-slate-600">{baselineFile.name} ({baselineFile.size} bytes)</p> : null}
        </label>
        <label className="rounded-sm border border-slate-300 bg-white p-4">
          <span className="text-sm font-medium">Latest JSON</span>
          <input
            data-testid="latest-input"
            ref={latestInputRef}
            className="mt-2 block w-full text-sm"
            type="file"
            accept="application/json"
            onChange={(event) => {
              abandonActiveRun();
              setError(null);
              setFileOrderAssessment(null);
              setPairOrderedBy(null);
              setLatestFile(event.target.files?.[0] ?? null);
            }}
          />
          {latestFile ? <p className="mt-2 text-xs text-slate-600">{latestFile.name} ({latestFile.size} bytes)</p> : null}
        </label>
      </section>
      {pairSize.level === "warn" ? (
        <p
          className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="pair-size-warning"
          role="status"
        >
          {pairSize.message}
        </p>
      ) : null}
      <section className="flex flex-wrap items-center gap-3 text-sm">
        <button
          type="button"
          data-testid="load-sample-data"
          className="rounded-sm border border-slate-300 bg-white px-3 py-1.5 font-medium hover:bg-slate-50 disabled:opacity-60"
          disabled={loadingSample || running}
          onClick={() => void loadSample()}
        >
          {loadingSample ? "Loading sample…" : "Try with sample data"}
        </button>
        <span className="text-xs text-slate-600">{SAMPLE_DESCRIPTION}</span>
      </section>
      {baselineFile && latestFile ? (
        <ExportDateIndicators baselineDates={baselineExportDates} latestDates={latestExportDates} issues={dateOrderingIssues} />
      ) : null}
      {dateOrderingIssues.length > 0 ? (
        <p className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="date-ordering-warning">
          Baseline Refreshed/Created dates should be older than the latest export. Review the highlighted dates before analyzing.
        </p>
      ) : null}
      <section className="grid gap-4 md:grid-cols-3">
        <label className="text-sm">
          <span className="mb-1 block font-medium">Collection path</span>
          <input
            className="w-full rounded-sm border border-slate-300 p-2"
            value={collectionPath}
            onChange={(event) => {
              setCollectionPath(event.target.value);
              setCollectionPathCustomized(event.target.value !== sourceProfile.collectionPath);
            }}
            placeholder="Export or $"
          />
          {collectionPathCustomized ? (
            <span className="mt-1 block text-xs text-amber-700" data-testid="collection-path-customized">
              Edited — differs from the profile ({sourceProfile.collectionPath}). Drift comparison will read “
              {collectionPath}”, but recovery always reads the profile's path.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => {
                  setCollectionPath(sourceProfile.collectionPath);
                  setCollectionPathCustomized(false);
                }}
              >
                Reset to profile
              </button>
            </span>
          ) : null}
        </label>
        <div className="block text-sm">
          <span className="font-medium">Source profile</span>
          <ProfilePicker
            profiles={profileRows}
            // The draft, when one governs, is what this control must name.
            value={activeDraft?.id ?? sourceProfileId}
            onChange={selectProfile}
            overriddenIds={overrideActive ? new Set([sourceProfile.id]) : undefined}
            draftIds={draftIds}
          />
          <span className="mt-1 block text-xs text-slate-500">
            Governs recovery: which fields may be backfilled, how records are matched, and when an
            export is blocked. Approved fields:{" "}
            {(resolvedProfile ?? sourceProfile).safeBackfillFields.length > 0
              ? (resolvedProfile ?? sourceProfile).safeBackfillFields.join(", ")
              : "none"}
            .{" "}
            <Link className="text-sky-700 underline" to={`/profiles?id=${sourceProfileId}`} data-testid="manage-profiles-link">
              Manage profiles
            </Link>
          </span>
          {overrideActive && resolvedProfile ? (
            <span
              data-testid="profile-override-badge"
              className="mt-1 block rounded-sm border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
            >
              Local override active: this analysis will run under repo v{sourceProfile.version} + override rev{" "}
              {resolvedProfile.overrideRevision} (policy {resolvedProfile.policyHash.slice(0, 8)}…), not the
              unmodified repo policy.
            </span>
          ) : null}
          {overrideStale ? (
            <span
              data-testid="profile-override-stale"
              className="mt-1 block rounded-sm border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
            >
              A local override exists for this profile but was written against an older repo version and was NOT
              applied. Review it on the Profiles page.
            </span>
          ) : null}
          {detection?.baseline.status === "match" &&
          detection.baseline.match.profileId === sourceProfileId &&
          selectionOrigin === "detected" ? (
            <span
              data-testid="profile-detection-notice"
              className="mt-1 block rounded-sm border border-sky-300 bg-sky-50 p-2 text-xs text-sky-800"
            >
              Detected from the uploaded file ({describeDetectionMatch(detection.baseline.match)}). Not this
              source? Pick another profile above.
            </span>
          ) : null}
          {detection?.baseline.status === "match" &&
          detection.baseline.match.profileId !== sourceProfileId &&
          !mismatchDismissed ? (
            <span
              data-testid="profile-detection-mismatch"
              className="mt-1 block rounded-sm border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
            >
              This file looks like{" "}
              <strong>{PROFILES[detection.baseline.match.profileId]?.displayName ?? detection.baseline.match.profileId}</strong>{" "}
              ({describeDetectionMatch(detection.baseline.match)}), but{" "}
              <strong>{sourceProfile.displayName ?? sourceProfile.id}</strong> is selected.{" "}
              <button
                type="button"
                className="underline"
                data-testid="use-detected-profile"
                onClick={() => {
                  const detected = detection.baseline;
                  if (detected.status === "match") applyProfileSelection(detected.match.profileId, "detected");
                }}
              >
                Use {PROFILES[detection.baseline.match.profileId]?.displayName ?? detection.baseline.match.profileId}
              </button>{" "}
              ·{" "}
              <button type="button" className="underline" onClick={() => setMismatchDismissedFor(pairId)}>
                Keep {sourceProfile.displayName ?? sourceProfile.id}
              </button>
            </span>
          ) : null}
          {detection?.baseline.status === "ambiguous" ? (
            <span
              data-testid="profile-detection-ambiguous"
              className="mt-1 block rounded-sm border border-slate-300 bg-slate-50 p-2 text-xs text-slate-600"
            >
              The uploaded file matches more than one profile (
              {detection.baseline.matches.map((match) => match.profileId).join(", ")}) — their detection rules
              overlap. Analysis is held until you pick the right one manually.
            </span>
          ) : null}
          {detection?.baseline.status === "none" && detection.latest.status !== "match" && activeDraft === null ? (
            <span
              data-testid="profile-detection-none"
              className="mt-1 block rounded-sm border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
            >
              No known source matched these files, so no profile has been chosen for them. Analysis is held until you
              pick one above. Whichever you pick governs this run completely — its backfill approvals and its
              match-rate floor included — so picking one that belongs to a different source would apply that source's
              approvals to this data.
              {parsedPair && !generating ? (
                <button
                  data-testid="generate-profile-button"
                  className="ml-2 underline"
                  onClick={() => setGenerating(true)}
                >
                  Or measure these files and propose a profile
                </button>
              ) : null}
            </span>
          ) : null}
          {crossSourcePair && detection?.baseline.status === "match" && detection.latest.status === "match" ? (
            <span
              data-testid="profile-detection-cross-source"
              className="mt-1 block rounded-sm border border-red-300 bg-red-50 p-2 text-xs text-red-700"
            >
              <span className="block font-semibold">
                These are two different sources. The analysis is held.
              </span>
              Baseline matches {detection.baseline.match.profileId}; latest matches{" "}
              {detection.latest.match.profileId}. No profile governs a comparison between two sources —
              the identity keys, the backfill approvals and the match-rate floor all belong to one of
              them — so every finding would be produced under policy that does not apply.
              <label className="mt-2 flex items-start gap-2">
                <input
                  type="checkbox"
                  data-testid="cross-source-acknowledge"
                  checked={crossSourceAcknowledged}
                  onChange={(event) => setCrossSourceAcknowledged(event.target.checked)}
                />
                <span>Compare them anyway. My reason is recorded in every artifact this run produces.</span>
              </label>
              {crossSourceAcknowledged ? (
                <input
                  className="mt-2 w-full rounded-sm border border-red-300 p-1 text-xs"
                  data-testid="cross-source-reason"
                  placeholder="Why compare two different sources?"
                  value={crossSourceReason}
                  onChange={(event) => setCrossSourceReason(event.target.value)}
                />
              ) : null}
              {crossSourceAcknowledged && crossSourceReason.trim().length === 0 ? (
                <span className="mt-1 block" data-testid="cross-source-reason-required">
                  A reason is required. It is quoted verbatim in the quality report.
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
        <label className="text-sm">
          <span className="mb-1 block font-medium">Identity fields (comma-separated)</span>
          <input
            className="w-full rounded-sm border border-slate-300 p-2"
            value={identityKeys}
            onChange={(event) => {
              setIdentityKeys(event.target.value);
              setIdentityCustomized(event.target.value !== sourceProfile.quality.identityDefault.join(","));
            }}
          />
          {identityCustomized ? (
            <span className="mt-1 block text-xs text-amber-700" data-testid="identity-customized">
              Edited — differs from the profile ({sourceProfile.quality.identityDefault.join(",")}).{" "}
              <button
                type="button"
                className="underline"
                onClick={() => {
                  setIdentityKeys(sourceProfile.quality.identityDefault.join(","));
                  setIdentityCustomized(false);
                }}
              >
                Reset to profile
              </button>
            </span>
          ) : null}
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium">Ignored fields</span>
          <input className="w-full rounded-sm border border-slate-300 p-2" value={ignoredFields} onChange={(event) => setIgnoredFields(event.target.value)} />
        </label>
      </section>
      {/* Full width, outside the three-column grid: a measurement table and a
          policy statement squeezed into a third of the page were unreadable,
          and both are the primary content when they are on screen. */}
      {activeDraft ? (
        <p
          data-testid="draft-profile-active"
          className="rounded-sm border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900"
        >
          Running under <strong>{activeDraft.displayName ?? activeDraft.id}</strong>, a draft measured from{" "}
          {activeDraft.draft.baselineFileName} and {activeDraft.draft.latestFileName}. It reports on this run but
          approves no backfill and releases no recovered export. Commit its delta to the repo to change that.
        </p>
      ) : null}
      {generating && parsedPair && baselineFile && latestFile ? (
        <GenerateProfilePanel
          baselineDataset={parsedPair.baseline}
          latestDataset={parsedPair.latest}
          baselineFileName={baselineFile.name}
          latestFileName={latestFile.name}
          onCancel={() => setGenerating(false)}
          onSave={async (row) => {
            const resolved = await saveDraft(row);
            setActiveDraft(resolved);
            setGenerating(false);
          }}
        />
      ) : null}
      <div className="flex items-center gap-4">
        <button
          data-testid="analyze-button"
          className="rounded-sm bg-sky-700 px-4 py-2 text-white disabled:bg-slate-400"
          disabled={disabled}
          onClick={onAnalyze}
          {...(unidentifiedSource || pairTooLarge ? { "aria-describedby": "analyze-blocked-reason" } : {})}
        >
          Analyze
        </button>
        {/* A disabled button with no stated reason is worse than the banner this
            replaced: the person can see it will not run but not why. The size
            refusal comes first — it holds however the profile question resolves. */}
        {pairTooLarge || unidentifiedSource ? (
          <p
            id="analyze-blocked-reason"
            data-testid="analyze-blocked-reason"
            className="text-sm text-amber-800"
          >
            {pairTooLarge && pairSize.level === "refuse"
              ? pairSize.message
              : "Pick a source profile above to run this analysis."}
          </p>
        ) : null}
        <div className="text-sm text-slate-600" aria-live="polite">{step ? `Progress: ${step}` : ""}</div>
      </div>
      {error ? <p className="rounded-sm border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
      {pendingAlert ? (
        <DateOrderingAlert
          baselineDates={baselineExportDates}
          issues={dateOrderingIssues}
          latestDates={latestExportDates}
          onCancel={() => setPendingAlert(false)}
          onContinue={() => {
            setPendingAlert(false);
            void runAnalysis();
          }}
        />
      ) : null}
      {/* Last on the page: a batch is the alternative to this screen, not a step
          in it, and offering it before the pair someone came to analyse would
          put the unusual path first. */}
      <BatchPanel />
    </main>
  );
}
